import React, { useState, FormEvent, useCallback, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL, getErrorMessage, isClosedBetaRegistrationError } from '../types';
import { useTranslation } from '../i18n';
import { useApiRequest } from '../useApiRequest';
import { ClosedBeta } from './ClosedBeta';
import { FormLabel } from './FormLabel';
import { SocialLoginButtons } from './SocialLoginButtons';
import { validateFormSubmit } from '../utils/formValidation';
import { normalizePhoneNumber } from '../utils/phoneNumber';
import { PhoneInput } from './PhoneInput';
import { TermsAcceptance, MarketingConsentCheckbox } from './TermsAcceptance';

interface AuthResponse {
  accessToken: string;
  refreshToken: string;
}

interface LoginProps {
  onLogin: (accessToken: string, refreshToken: string) => void;
  setError: (error: string) => void;
  inline?: boolean;
  onLoginSuccess?: () => void;
}

export const Login: React.FC<LoginProps> = ({ onLogin, setError, inline, onLoginSuccess }) => {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'error' | 'success' | null>(null);
  const [registrationClosed, setRegistrationClosed] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const externalError = searchParams.get('external_error');
    if (externalError) {
      setMessage(externalError);
      setMessageType('error');
      searchParams.delete('external_error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (searchParams.get('closed_beta') !== '1') return;
    setEmail(searchParams.get('email') ?? '');
    setFirstName(searchParams.get('firstName') ?? '');
    setLastName(searchParams.get('lastName') ?? '');
    setRegistrationClosed(true);
    searchParams.delete('closed_beta');
    searchParams.delete('email');
    searchParams.delete('firstName');
    searchParams.delete('lastName');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const { loading, call } = useApiRequest(async () => {
    const endpoint = isRegistering ? '/auth/register' : '/auth/login';
    const payload = isRegistering
      ? { username, password, email, firstName: firstName.trim(), lastName: lastName.trim(), phoneNumber: normalizePhoneNumber(phoneNumber), acceptTerms, marketingConsent }
      : { username, password };
    if (isRegistering) {
      await axios.post(`${API_URL}${endpoint}`, payload);
      setIsRegistering(false);
      setMessage(t('login.register_success'));
      setMessageType('success');
    } else {
      const res = await axios.post<AuthResponse>(`${API_URL}${endpoint}`, payload);
      onLogin(res.data.accessToken, res.data.refreshToken);
      setError('');
      onLoginSuccess?.();
    }
  });

  const handleSubmit = useCallback(async (e: FormEvent) => {
    if (!validateFormSubmit(e)) return;
    e.preventDefault();
    setMessage('');
    setMessageType(null);
    setRegistrationClosed(false);
    try {
      await call();
    } catch (err) {
      if (isRegistering && isClosedBetaRegistrationError(err)) {
        setRegistrationClosed(true);
        return;
      }
      setMessage(getErrorMessage(err, t('login.generic_error')));
      setMessageType('error');
    }
  }, [call, t, isRegistering]);

  const form = registrationClosed ? (
    <>
      <ClosedBeta
        inline
        email={email}
        firstName={firstName.trim()}
        lastName={lastName.trim()}
      />
      <div className="form-actions" style={{ marginTop: '1rem' }}>
        <button
          type="button"
          className="btn btn-secondary"
          style={{ width: '100%' }}
          onClick={() => {
            setRegistrationClosed(false);
            setIsRegistering(false);
          }}
        >
          {t('login.switch_to_login')}
        </button>
      </div>
    </>
  ) : (
    <>
      <h2 style={inline ? { margin: '0 0 0.25rem' } : undefined}>{t('login.title')}</h2>
      <p className="login-subtitle">{isRegistering ? t('login.subtitle_register') : t('login.subtitle_login')}</p>

      {message && (
        <div
          className={`alert ${messageType === 'success' ? 'alert-success' : 'alert-error'}`}
          style={{ marginBottom: '1rem' }}
        >
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <FormLabel required>{isRegistering ? t('login.username') : t('login.username_or_email')}</FormLabel>
          <input type="text" value={username} onChange={e => setUsername(e.target.value)} required disabled={loading} aria-required="true" />
        </div>
        {isRegistering && (
          <>
            <div className="form-group">
              <FormLabel required>{t('login.first_name')}</FormLabel>
              <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} required disabled={loading} autoComplete="given-name" aria-required="true" />
            </div>
            <div className="form-group">
              <FormLabel required>{t('login.last_name')}</FormLabel>
              <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} required disabled={loading} autoComplete="family-name" aria-required="true" />
            </div>
            <div className="form-group">
              <FormLabel required>{t('login.email')}</FormLabel>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required disabled={loading} aria-required="true" />
            </div>
            <div className="form-group">
              <FormLabel required>{t('login.phone_number')}</FormLabel>
              <PhoneInput
                value={phoneNumber}
                onChange={setPhoneNumber}
                placeholder={t('login.phone_placeholder')}
                required
                disabled={loading}
                aria-required="true"
                invalidMessage={t('validation.phone_invalid')}
              />
            </div>
          </>
        )}
        <div className="form-group">
          <FormLabel required>{t('login.password')}</FormLabel>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} required disabled={loading} aria-required="true" />
        </div>
        {isRegistering && (
          <>
            <TermsAcceptance checked={acceptTerms} onChange={setAcceptTerms} disabled={loading} />
            <MarketingConsentCheckbox checked={marketingConsent} onChange={setMarketingConsent} disabled={loading} />
          </>
        )}
        <div className="form-actions" style={{ flexDirection: 'column', gap: '0.5rem' }}>
          <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={loading}>
            {loading && <span className="btn-spinner" aria-hidden="true" />}
            {isRegistering ? t('login.submit_register') : t('login.submit_login')}
          </button>
          {!isRegistering && (
            <div style={{ textAlign: 'center' }}>
              <Link to="/forgot-password" className="forgot-link">{t('login.forgot_password')}</Link>
            </div>
          )}
        </div>
      </form>
      <div className="form-actions" style={{ flexDirection: 'column', gap: '0.5rem' }}>
        <SocialLoginButtons />
        <button
          type="button"
          className="btn btn-secondary"
          style={{ width: '100%' }}
          onClick={() => {
            setIsRegistering(!isRegistering);
            setMessage('');
            setMessageType(null);
            setRegistrationClosed(false);
          }}
        >
          {isRegistering ? t('login.switch_to_login') : t('login.switch_to_register')}
        </button>
      </div>
    </>
  );

  if (inline) {
    return form;
  }

  return (
    <div className="login-page">
      <div className="login-card">{form}</div>
    </div>
  );
};
